import React, { useEffect, useRef, useState } from "react";
import { Animated, Pressable, ScrollView, StyleSheet, View, useWindowDimensions } from "react-native";
import { CheckCircleIcon, SpinnerGapIcon, XIcon } from "@phosphor-icons/react";

import CustomText from "../../../common/general/CustomText/CustomText";
import {
  RubricParseStatus,
  type ActiveRubricSetQuery,
  type RubricSetParseStatusQuery,
} from "../../../../graphql/generated/graphql";
import RubricProcessing from "./RubricProcessing";
import RubricReady from "./RubricReady";
import RubricUpload, { type RubricUploadState } from "./RubricUpload";

type RubricSetViewModel = NonNullable<ActiveRubricSetQuery["activeRubricSet"]["data"]>;
type RubricSetStatusViewModel = NonNullable<RubricSetParseStatusQuery["rubricSet"]["data"]>;
type RubricDisplayViewModel = RubricSetViewModel | RubricSetStatusViewModel;

interface RubricConfigSlideInProps {
  visible: boolean;
  onClose: () => void;
  rubric: RubricDisplayViewModel | null;
  loadingRubric: boolean;
  selectedFileName: string | null;
  selectedFileSize: number | null;
  uploadState: RubricUploadState;
  uploadError: string | null;
  allowedMimeTypes: string[];
  maxFileSize: number;
  onSelectFile: () => void;
  onClearFile: () => void;
  onParseRubric: () => void;
}

const inProgressStatuses = [RubricParseStatus.Pending, RubricParseStatus.Parsing];

const hasRubricSections = (rubric: RubricDisplayViewModel): rubric is RubricSetViewModel => "sections" in rubric;

function RubricConfigSlideIn({
  visible,
  onClose,
  rubric,
  loadingRubric,
  selectedFileName,
  selectedFileSize,
  uploadState,
  uploadError,
  allowedMimeTypes,
  maxFileSize,
  onSelectFile,
  onClearFile,
  onParseRubric,
}: RubricConfigSlideInProps) {
  const [isMounted, setIsMounted] = useState(false);
  const { width } = useWindowDimensions();
  const panelWidth = Math.min(480, width);
  const slideAnim = useRef(new Animated.Value(panelWidth)).current;

  useEffect(() => {
    if (visible) {
      setIsMounted(true);
    }
    Animated.timing(slideAnim, {
      toValue: visible ? 0 : panelWidth,
      duration: 300,
      useNativeDriver: true,
    }).start(({ finished }) => {
      if (!visible && finished) {
        setIsMounted(false);
      }
    });
  }, [panelWidth, slideAnim, visible]);

  if (!isMounted) {
    return null;
  }

  const showReadyRubric = rubric?.parseStatus === RubricParseStatus.Ready;
  const showProcessingRubric = rubric != null && inProgressStatuses.includes(rubric.parseStatus);
  const showFailedRubric = rubric?.parseStatus === RubricParseStatus.Failed;

  return (
    <Animated.View style={[styles.panel, { width: panelWidth, transform: [{ translateX: slideAnim }] }]}>
      <View style={styles.panelContainer}>
        <View style={styles.topRow}>
          <View style={styles.headerCopy}>
            <CustomText
              text="Evaluation Configuration"
              size="xl"
              weight="bold"
              style={styles.headerText}
              useTranslationText={false}
            />
            <CustomText
              text="Rubric, guidelines, and policies used during course evaluation"
              size="s"
              style={styles.secondaryText}
              useTranslationText={false}
            />
          </View>
          <Pressable role="button" aria-label="Close rubric configuration" style={styles.iconButton} onPress={onClose}>
            <XIcon color="#344054" size={16} />
          </Pressable>
        </View>

        <ScrollView style={styles.contentContainer}>
          <View style={styles.contentInner}>
            {loadingRubric ? (
              <View style={styles.processingPanel}>
                <SpinnerGapIcon color="#3E68FE" size={28} weight="bold" />
                <CustomText text="Loading rubric..." size="m" weight="bold" useTranslationText={false} />
              </View>
            ) : showReadyRubric && rubric && hasRubricSections(rubric) ? (
              <RubricReady rubric={rubric} />
            ) : showProcessingRubric && rubric ? (
              <RubricProcessing rubric={rubric} />
            ) : (
              <>
                {showFailedRubric && rubric ? <RubricProcessing rubric={rubric} /> : null}
                <RubricUpload
                  selectedFileName={selectedFileName}
                  selectedFileSize={selectedFileSize}
                  uploadState={uploadState}
                  uploadError={uploadError}
                  allowedMimeTypes={allowedMimeTypes}
                  maxFileSize={maxFileSize}
                  onSelectFile={onSelectFile}
                  onClearFile={onClearFile}
                  onParseRubric={onParseRubric}
                />
              </>
            )}
          </View>
        </ScrollView>

        {showReadyRubric ? (
          <View style={styles.footer}>
            <View style={styles.footerStatus}>
              <CheckCircleIcon color="#027A48" size={18} weight="fill" />
              <CustomText
                text="Rubric is active and ready for course modernization."
                size="xs"
                style={styles.successText}
                useTranslationText={false}
              />
            </View>
          </View>
        ) : null}
      </View>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  panel: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    zIndex: 100,
    backgroundColor: "#FFFFFF",
    // @ts-expect-error - react-native-web accepts viewport units
    height: "100vh",
  },
  panelContainer: {
    flex: 1,
    backgroundColor: "#FFFFFF",
    borderLeftWidth: 1,
    borderLeftColor: "#EAECF0",
  },
  topRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    paddingHorizontal: 24,
    paddingTop: 24,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#EAECF0",
    gap: 16,
  },
  headerText: {
    color: "#101828",
    fontSize: 20,
    lineHeight: 30,
  },
  headerCopy: {
    flex: 1,
    gap: 2,
  },
  iconButton: {
    alignItems: "center",
    justifyContent: "center",
    minWidth: 36,
    minHeight: 36,
    padding: 8,
    borderRadius: 8,
  },
  contentContainer: {
    flex: 1,
  },
  contentInner: {
    paddingHorizontal: 24,
    paddingVertical: 16,
  },
  secondaryText: {
    color: "#344054",
  },
  successText: {
    color: "#027A48",
  },
  processingPanel: {
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    minHeight: 260,
    paddingHorizontal: 20,
    paddingVertical: 28,
    borderRadius: 8,
    backgroundColor: "#FFFFFF",
  },
  footer: {
    paddingHorizontal: 24,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: "#EAECF0",
  },
  footerStatus: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
});

export default RubricConfigSlideIn;
